import _ from 'lodash';

import { Router } from '@vaadin/router/dist/vaadin-router';
import {
  ApiError,
  NotFoundError,
  RequestTimeoutError,
  ServerError,
  UnauthenticatedError,
  UnauthorizedError,
} from '../../components/errors';
import { OktaAuthService } from './okta-auth-service';

export interface ApiHeaders {
  [key: string]: string;
}

export interface ApiErrorResponse {
  errorCode?: string;
  message?: string;
}

export interface ApiConfig {
  timeout?: number;
  retryAttempts?: number;
}

// sessionStorage key for the re-auth attempt counter (loop-breaker for the
// 401 → reauth → 401 cycle). Incremented in handle401() before each reauthenticate()
// call; cleared on the first successful 2xx API response in performFetch, or by the
// explicit "Refresh page" action on the 401 error page (error-page.tsx refreshTo401Root).
// sessionStorage is used because it survives the cross-origin Okta round-trip
// within the same tab.
// The loop-breaker in handle401() reads this key: if the counter has reached
// REAUTH_MAX_ATTEMPTS when a fresh 401 arrives, the re-issued token is still being
// rejected, so we stop redirecting and show the error page instead.
// Exported so error-page.tsx can clear it on the explicit Refresh action.
export const REAUTH_ATTEMPT_KEY = 'cc-okta-reauth-attempt';

// Maximum number of completed reauth round-trips before we give up and show the
// error page. Set to 1: the first persistent 401 triggers a single re-auth; if the
// re-issued token is STILL rejected on the next 401 we stop. This is
// duration-independent — it counts completed round-trips, not elapsed time, so a
// slow login (MFA, network delay) cannot exceed the window and loop indefinitely.
const REAUTH_MAX_ATTEMPTS = 1;

export class CCApiService {
  authEnabled = false;
  oktaService: OktaAuthService;
  private config: ApiConfig;

  constructor(authClientId?: string, authIssuerUri?: string, config: ApiConfig = {}) {
    if (authClientId && authIssuerUri) {
      this.authEnabled = true;
      this.oktaService = new OktaAuthService(authClientId, authIssuerUri);
    }
    this.config = {
      timeout: 120000, // 2 minutes default
      retryAttempts: 0,
      ...config,
    };
  }

  performFetch = async <T = unknown>(url: string, request?: RequestInit): Promise<T> => {
    request = request || { method: 'GET' };

    const headers: ApiHeaders = {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    };

    if (this.authEnabled) {
      const tokens = await this.oktaService.getTokens();
      if (!tokens.accessToken?.tokenType || !tokens.accessToken?.accessToken) {
        const missingAccessTokenMessage = 'Unable to get access token for Authorization header (auth is enabled)';
        console.error(missingAccessTokenMessage);
        return Promise.reject(missingAccessTokenMessage);
      }
      headers.Authorization = `${tokens.accessToken.tokenType} ${tokens.accessToken.accessToken}`;
    }

    request.headers = _.defaults(request.headers || {}, headers);

    // Add timeout to request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);
    request.signal = controller.signal;

    let response: Response;
    try {
      response = await fetch(url, request);
      clearTimeout(timeoutId);
    } catch (err) {
      clearTimeout(timeoutId);

      if (err.name === 'AbortError') {
        const timeoutError = new RequestTimeoutError(`Request timeout after ${this.config.timeout}ms: ${url}`);
        console.error(timeoutError.message);
        throw timeoutError;
      }

      console.error('Fetch error:', err);

      // Low-level handling for verifying auth token whenever fetch is failed
      if (this.authEnabled) {
        this.oktaService.authGuard();
      }
      throw new Error(err);
    }

    let json: T;
    try {
      json = await this.parseJson<T>(response);
    } catch (error) {
      if (error instanceof UnauthenticatedError) {
        this.handle401();
      }
      throw error;
    }
    // A successful response from an authenticated service proves the server accepted
    // the current token. Reset the attempt counter so a later, unrelated 401 gets a
    // fresh re-auth attempt instead of being mistaken for a redirect loop.
    // Only clear when authEnabled: unauthenticated endpoints (e.g. /ui-config/props.json)
    // succeed regardless of token validity and must not reset the counter mid-session.
    if (this.authEnabled) {
      sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
    }
    return json;
  };

  handle401() {
    // Without Okta we can't re-auth — just surface the error page.
    if (!this.oktaService) {
      Router.go('error/401');
      return;
    }

    // Transient guard: if an auth redirect/reauthenticate is currently in flight,
    // every concurrent 401 is transient — the redirect will resolve auth. Suppress
    // unconditionally so a burst of concurrent API calls that all 401 (stale-token
    // returning-user path) does not race to show error/401 before the redirect
    // resolves. The attempt counter is checked below for the post-return case.
    if (this.oktaService.isAuthInFlight) {
      return;
    }

    // Loop breaker: if we have already completed REAUTH_MAX_ATTEMPTS round-trips
    // and STILL get a 401, the re-issued token isn't being accepted. Stop
    // redirecting and show the error page so we don't loop between the app and
    // Okta. This counter is duration-independent: a slow login (MFA, network
    // delay > 30 s) cannot overflow the window — it counts completed round-trips.
    //
    // The counter is intentionally PRESERVED at max here (NOT cleared). Concurrent
    // sibling 401s that arrive before Router.go completes re-read the counter, find
    // it still at max, and also short-circuit to error/401 — without calling
    // reauthenticate(). This prevents the sibling re-loop structurally, with no
    // isAuthInFlight flag needed. The flag is NOT set here (KUMO-1322): leaving it
    // set across client-side navigations away from /error/401 caused later 401s to
    // be silently suppressed forever. The counter is only cleared by a successful
    // 2xx response (performFetch) or by the explicit "Refresh page" action in
    // error-page.tsx refreshTo401Root() — which also performs a full-document
    // reload so module state resets cleanly.
    const attempts = Number(sessionStorage.getItem(REAUTH_ATTEMPT_KEY) || '0');
    if (attempts >= REAUTH_MAX_ATTEMPTS) {
      Router.go('error/401');
      return;
    }

    // Increment before redirecting so the post-return 401 sees attempts = 1.
    sessionStorage.setItem(REAUTH_ATTEMPT_KEY, String(attempts + 1));
    // Capture where we are and bounce through Okta. On success the SDK restores
    // the original URI, dropping the user back exactly where they were.
    this.oktaService.reauthenticate().catch((err) => console.error('reauthenticate failed', err));
  }

  async parseJson<T = unknown>(response: Response): Promise<T> {
    switch (response.status) {
      case 200:
      case 201:
        try {
          const json: T = await response.json();
          return Promise.resolve(json);
        } catch {
          const text = await response.text().catch(() => '');
          throw new Error(text?.trim() || 'Response was not valid JSON');
        }
      case 204:
        return Promise.resolve(undefined as T);
      case 401:
        throw new UnauthenticatedError();
      case 403:
        throw new UnauthorizedError();
      case 404:
        throw new NotFoundError();
      case 500:
        throw new ServerError();
      default: {
        let error: Error;
        try {
          const errorJson: ApiErrorResponse = await response.json();
          // JSON response — preserve ApiError for errorCode so capability detection works
          error = errorJson.errorCode ? new ApiError(errorJson.errorCode) : new Error(errorJson.message);
        } catch {
          // Not JSON — gateway timeout, upstream error, etc. Use the plain text body.
          const text = await response.text().catch(() => '');
          error = new Error(text?.trim() || `Request failed (${response.status})`);
        }
        // Include status code in error object for capability error detection
        (error as any).statusCode = response.status;
        throw error;
      }
    }
  }
}
